import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const inputCls =
  "w-full bg-[#0C131D] border border-white/10 rounded-lg px-3.5 py-2.5 text-sm text-white placeholder:text-brand-textMuted focus:outline-none focus:ring-2 focus:ring-brand-cyan";

const Field = ({ field, value, onChange }) => {
  const v = value ?? "";
  if (field.type === "textarea") {
    return <textarea rows={field.big ? 8 : 3} value={v} onChange={(e) => onChange(e.target.value)} className={inputCls} data-testid={`field-${field.key}`} />;
  }
  if (field.type === "checkbox") {
    return (
      <label className="flex items-center gap-2 cursor-pointer">
        <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4 accent-[#25C4D8]" data-testid={`field-${field.key}`} />
        <span className="text-sm text-brand-textSec">{field.label}</span>
      </label>
    );
  }
  if (field.type === "select") {
    return (
      <select value={v} onChange={(e) => onChange(e.target.value)} className={inputCls} data-testid={`field-${field.key}`}>
        <option value="">—</option>
        {field.options.map((o) => <option key={o} value={o} className="bg-[#0C131D]">{o}</option>)}
      </select>
    );
  }
  if (field.type === "number") {
    return <input type="number" value={v} onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))} className={inputCls} data-testid={`field-${field.key}`} />;
  }
  if (field.type === "tags") {
    const str = Array.isArray(value) ? value.join(", ") : v;
    return <input type="text" value={str} onChange={(e) => onChange(e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} className={inputCls} data-testid={`field-${field.key}`} />;
  }
  return <input type="text" required={field.required} value={v} onChange={(e) => onChange(e.target.value)} className={inputCls} data-testid={`field-${field.key}`} />;
};

export const CrudManager = ({ config }) => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // object or null
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [ytUrl, setYtUrl] = useState("");
  const [fetching, setFetching] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(config.endpoint + (config.listParams || ""));
      setItems(data);
    } catch {
      toast.error("Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.endpoint]);

  const openNew = () => {
    setForm({ ...config.empty });
    setYtUrl("");
    setEditing("new");
  };
  const openEdit = (item) => {
    const f = {};
    config.fields.forEach((fl) => (f[fl.key] = item[fl.key] ?? config.empty[fl.key]));
    setForm(f);
    setYtUrl("");
    setEditing(item.id);
  };
  const close = () => {
    setEditing(null);
    setForm({});
    setYtUrl("");
  };

  const fetchMeta = async () => {
    if (!ytUrl.trim()) return;
    setFetching(true);
    try {
      const { data } = await api.get("/videos/fetch-meta", { params: { url: ytUrl.trim() } });
      setForm((f) => ({
        ...f,
        youtube_id: data.youtube_id || f.youtube_id,
        title: data.title || f.title,
        thumbnail: data.thumbnail || f.thumbnail,
      }));
      toast.success("Pulled in the video details — edit anything before saving.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't fetch video details");
    } finally {
      setFetching(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing === "new") {
        await api.post(config.endpoint, form);
        toast.success("Created");
      } else {
        await api.put(`${config.endpoint}/${editing}`, form);
        toast.success("Updated");
      }
      close();
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    try {
      await api.delete(`${config.endpoint}/${id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div data-testid={`crud-${config.endpoint.slice(1)}`}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-display font-bold text-2xl text-white">{config.label}</h2>
          <p className="text-brand-textMuted text-sm">{items.length} item(s)</p>
        </div>
        <button onClick={openNew} className="btn-primary px-4 py-2.5 text-sm inline-flex items-center gap-2" data-testid="crud-new-btn">
          <Plus size={16} /> New
        </button>
      </div>

      {loading ? (
        <p className="text-brand-textMuted">Loading…</p>
      ) : (
        <div className="space-y-2">
          {items.map((item) => (
            <div key={item.id} className="flex items-center justify-between gap-4 bg-[#0C131D] border border-white/10 rounded-lg px-4 py-3" data-testid={`crud-item-${item.id}`}>
              <div className="min-w-0">
                <p className="text-white font-medium text-sm truncate">{item[config.titleField] || "(untitled)"}</p>
                {config.subField && item[config.subField] && (
                  <p className="text-brand-textMuted text-xs truncate">{item[config.subField]}</p>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button onClick={() => openEdit(item)} className="h-8 w-8 grid place-items-center rounded-md border border-white/10 text-brand-textSec hover:text-brand-cyan hover:border-brand-cyan/40" data-testid={`crud-edit-${item.id}`}>
                  <Pencil size={15} />
                </button>
                <button onClick={() => remove(item.id)} className="h-8 w-8 grid place-items-center rounded-md border border-white/10 text-brand-textSec hover:text-red-400 hover:border-red-400/40" data-testid={`crud-delete-${item.id}`}>
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          ))}
          {items.length === 0 && <p className="text-brand-textMuted text-sm">No items yet.</p>}
        </div>
      )}

      {editing !== null && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto" data-testid="crud-modal">
          <div className="bg-[#0C131D] border border-white/10 rounded-xl w-full max-w-2xl my-8">
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 sticky top-0 bg-[#0C131D] rounded-t-xl">
              <h3 className="font-display font-semibold text-white">{editing === "new" ? "New" : "Edit"} — {config.label}</h3>
              <button onClick={close} className="text-brand-textMuted hover:text-white" data-testid="crud-modal-close"><X size={20} /></button>
            </div>
            <form onSubmit={save} className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
              {config.autofillYoutube && (
                <div className="rounded-lg border border-brand-cyan/30 bg-brand-cyan/5 p-4" data-testid="video-autofill">
                  <label className="text-sm text-white font-medium mb-1.5 block">Paste YouTube URL to auto-fill</label>
                  <div className="flex gap-2">
                    <input value={ytUrl} onChange={(e) => setYtUrl(e.target.value)} placeholder="https://www.youtube.com/watch?v=…"
                      className={inputCls} data-testid="video-url-input"
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); fetchMeta(); } }} />
                    <button type="button" onClick={fetchMeta} disabled={fetching || !ytUrl.trim()}
                      className="btn-primary px-4 py-2.5 text-sm whitespace-nowrap disabled:opacity-50" data-testid="video-url-fetch">
                      {fetching ? "Fetching…" : "Fetch details"}
                    </button>
                  </div>
                  <p className="text-brand-textMuted text-xs mt-1.5">Pulls the title, thumbnail and video ID automatically. Add the description below — all fields stay editable.</p>
                </div>
              )}
              {config.fields.map((field) => (
                <div key={field.key}>
                  {field.type !== "checkbox" && <label className="text-sm text-brand-textSec mb-1.5 block">{field.label}</label>}
                  <Field field={field} value={form[field.key]} onChange={(val) => setForm((f) => ({ ...f, [field.key]: val }))} />
                  {field.hint && <p className="text-brand-textMuted text-xs mt-1">{field.hint}</p>}
                </div>
              ))}
              <div className="flex gap-3 pt-2">
                <button type="submit" className="btn-primary px-5 py-2.5 text-sm" data-testid="crud-save-btn">Save</button>
                <button type="button" onClick={close} className="btn-ghost px-5 py-2.5 text-sm">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
